import React, { useEffect, useRef, useState } from "react";
import api from "../api";

/**
 * Renders a PayPal "Subscribe" button for ONE specific plan tier, and once
 * the user approves it, tells our own backend to verify + record the
 * subscription — the frontend alone is never trusted as proof of payment.
 *
 * Props:
 *   clientId  - PayPal client ID (same for all plans, comes from /api/billing/paypal/plans)
 *   planId    - the PayPal plan_id for THIS specific tier (e.g. Silver's plan_id)
 *   planKey   - our own internal tier name: "silver" | "gold" | "diamond"
 *   onSubscribed - called after a successful activation, so the parent can refresh
 */
export default function PayPalSubscribeButton({ clientId, planId, planKey, onSubscribed }) {
  const containerRef = useRef(null);
  const [status, setStatus] = useState(""); // "", "verifying", "done", "error"
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!clientId || !planId) return;

    const renderButton = () => {
      if (!window.paypal || !containerRef.current) return;
      containerRef.current.innerHTML = ""; // clear in case of re-render

      window.paypal.Buttons({
        style: { shape: "rect", color: "gold", layout: "vertical", label: "subscribe" },
        createSubscription: (data, actions) => {
          return actions.subscription.create({ plan_id: planId });
        },
        onApprove: async (data) => {
          setStatus("verifying");
          setMessage("Confirming your subscription...");
          try {
            const res = await api.post("/api/billing/paypal/activate", {
              subscription_id: data.subscriptionID,
              plan_key: planKey,
            });
            setStatus("done");
            setMessage("Subscription activated!");
            onSubscribed && onSubscribed(res.data);
          } catch (err) {
            setStatus("error");
            setMessage(
              err.response?.data?.error ||
                "Could not verify subscription — contact support with your subscription ID: " + data.subscriptionID
            );
          }
        },
        onError: () => {
          setStatus("error");
          setMessage("PayPal reported an error. Please try again.");
        },
      }).render(containerRef.current);
    };

    // Each plan tier needs the SDK loaded with the same client-id — load it
    // once (shared across all 3 buttons on the Billing page) and re-render
    // this specific button once it's ready.
    const existing = document.getElementById("paypal-sdk-script");
    if (existing) {
      if (window.paypal) {
        renderButton();
      } else {
        existing.addEventListener("load", renderButton);
      }
    } else {
      const script = document.createElement("script");
      script.id = "paypal-sdk-script";
      script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&vault=true&intent=subscription`;
      script.onload = renderButton;
      document.body.appendChild(script);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, planId, planKey]);

  return (
    <div>
      <div ref={containerRef} />
      {message && (
        <p style={{ marginTop: 10, fontSize: 13, color: status === "error" ? "#dc2626" : "#16a34a" }}>
          {message}
        </p>
      )}
    </div>
  );
}